import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Settings,
  CheckCircle, Circle, ChevronRight, ChevronDown, 
  ArrowLeft, Clock, BookOpen, Star, Share2, 
  Download, MessageSquare, Info, Award, Loader2
} from 'lucide-react';
import CourseHeader from '../../components/CourseHeader';
import { useNavigate, useLocation } from 'react-router-dom';
import { postRequest, getRequest } from '../../utils/networking';
import { apiUrls, videoUrl as videoBaseUrl, baseUrl } from '../../utils/url';
import { toast } from 'react-toastify';

const PlaybackSpeedIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M10 8L16 12L10 16V8Z" fill="currentColor"/>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4"/>
  </svg>
);

const CourseDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const courseId = location.state?.id;
  const videoRef = useRef(null);
  const lastSaveTimeRef = useRef(0);

  const [topics, setTopics] = useState([]);
  const [courseInfo, setCourseInfo] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [expandedSyllabus, setExpandedSyllabus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Video player states
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [durationStr, setDurationStr] = useState('0:00');
  const [currentTimeStr, setCurrentTimeStr] = useState('0:00');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showControls, setShowControls] = useState(true);
  
  const controlsTimeoutRef = useRef(null);
  const playerContainerRef = useRef(null);

  // Send video progress to backend (throttled to every 10 seconds)
  const sendProgress = useCallback(async (current, duration) => {
    const now = Date.now();
    if (now - lastSaveTimeRef.current < 10000) return; // skip if less than 10s since last save
    lastSaveTimeRef.current = now;
    try {
      const payload = {
        courseid: courseId,
        curriculamid: selectedVideo?.id,
        videoUrl: selectedVideo?.videoUrl,
        watched_seconds: Math.floor(current),
        duration_seconds: Math.floor(duration || 0),
        progress_percent: duration ? Math.floor((current / duration) * 100) : 0,
        last_position_seconds: Math.floor(current),
        iscompleted: duration ? Math.floor((current / duration) * 100) >= 90 : false
      };
      const saveResp = await postRequest(apiUrls.saveprogress, payload);
      console.log('saveprogress response:', saveResp);

      // After successful save, refresh progress data from server
      try {
        const progResp = await postRequest(apiUrls.getprogress);
        console.log('getprogress response (after save):', progResp);
        let progressData = [];
        if (progResp?.data && Array.isArray(progResp.data)) {
          progressData = progResp.data;
        } else if (Array.isArray(progResp)) {
          progressData = progResp;
        }

        if (progressData.length > 0) {
          const progressMap = {};
          progressData.forEach(p => {
            if (p.courseid) progressMap[`${p.courseid}_${p.curriculamid}`] = p;
            progressMap[p.curriculamid] = p;
          });

          // Update topics with fresh progress data
          setTopics(prev => prev.map(topic => ({
            ...topic,
            items: topic.items.map(item => {
              const pData = progressMap[`${courseId}_${item.id}`] || progressMap[item.id] || {};
              return {
                ...item,
                isCompleted: pData.iscompleted === true || pData.iscompleted === "true" || Math.round(pData.progress_percent || 0) >= 100,
                progressPercent: pData.progress_percent || item.progressPercent,
                lastPosition: pData.last_position_seconds || pData.watched_seconds || item.lastPosition || 0
              };
            })
          })));
        }
      } catch (err) {
        console.error('Error refreshing progress after save:', err);
      }
    } catch (err) {
      console.error('Error saving progress', err);
    }
  }, [courseId, selectedVideo]);

  // Restart video from beginning
  const handleWatchAgain = async () => {
    const videoEl = document.getElementById('course-video');
    if (videoEl) {
      videoEl.currentTime = 0;
      videoEl.play();
    }
  };

  useEffect(() => {
    if (!courseId) {
      navigate('/mycourse');
    }
  }, [courseId, navigate]);

  const getFullAssetUrl = (path) => {
    if (!path) return null;
    if (String(path).startsWith('http')) return path;
    
    const normalized = String(path).replace(/\\/g, "/");
    
    // If path contains known directory markers, serve from CDN
    if (normalized.includes("ecomtamil/")) {
      const extractedPath = normalized.split("ecomtamil/").pop().replace(/^\/+/, '');
      return `${videoBaseUrl}/${extractedPath}`;
    }
    if (normalized.includes("assets/")) {
      const assetsIndex = normalized.indexOf("assets/");
      const extractedPath = normalized.substring(assetsIndex + 7).replace(/^\/+/, '');
      return `${videoBaseUrl}/${extractedPath}`;
    }
    
    // Raw UUID or filename — serve from CDN instead of API server
    const cleanPath = normalized.replace(/^\/+/, '');
    return `${videoBaseUrl}/coursevideos/${cleanPath}`;
  };

  const getAttachmentUrl = (v) => {
    const rawPath = v.fileurl || v.url || v.path || v.attachment_url || v.variant_url;
    if (!rawPath) return null;
    if (String(rawPath).startsWith('http')) return rawPath;
    
    const ext = (v.filetype || v.type || '').toLowerCase();
    const normalized = String(rawPath).replace(/\\/g, "/").replace(/^\/+/, '');
    const fileName = ext && !normalized.toLowerCase().endsWith(`.${ext}`) 
      ? `${normalized}.${ext}` 
      : normalized;
      
    // If path contains known directory markers, serve from CDN
    if (normalized.includes("ecomtamil/")) {
      const extractedPath = normalized.split("ecomtamil/").pop().replace(/^\/+/, '');
      return `${videoBaseUrl}/${extractedPath}`;
    }
    if (normalized.includes("assets/")) {
      const assetsIndex = normalized.indexOf("assets/");
      const extractedPath = normalized.substring(assetsIndex + 7).replace(/^\/+/, '');
      return `${videoBaseUrl}/${extractedPath}`;
    }
    
    // Serve from CDN directly instead of API
    return `${videoBaseUrl}/coursevideos/${fileName}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 1. Fetch Course Info from My Course Orders
        const ordersResponse = await postRequest(apiUrls.getmycourseorders);
        if (ordersResponse.status === "S" || ordersResponse.status === "s") {
          const myCourse = ordersResponse.data.find(c => String(c.courseid) === String(courseId));
          if (myCourse) {
            setCourseInfo(myCourse);
          }
        }

        // 2. Fetch Curriculum
        const curriculumResponse = await postRequest(apiUrls.getcurriculams, { 
          courseid: courseId,
          id: courseId 
        });

        console.log("Curriculum response:", curriculumResponse);

        // 3. Fetch Progress
        let progressData = [];
        try {
          const progResp = await postRequest(apiUrls.getprogress);
          console.log("getprogress response:", progResp);
          if (progResp?.data && Array.isArray(progResp.data)) {
            progressData = progResp.data;
          } else if (Array.isArray(progResp)) {
            progressData = progResp;
          }
        } catch (error) {
          console.error("Error fetching progress:", error);
        }

        const progressMap = {};
        progressData.forEach(p => {
          if (p.courseid) {
            progressMap[`${p.courseid}_${p.curriculamid}`] = p;
          }
          progressMap[p.curriculamid] = p; // Fallback
        });

        let syllabusData = [];
        if (curriculumResponse.status === "S" && Array.isArray(curriculumResponse.data)) {
          syllabusData = curriculumResponse.data;
        } else if (Array.isArray(curriculumResponse)) {
          syllabusData = curriculumResponse;
        } else if (typeof curriculumResponse === 'object') {
          for (let key in curriculumResponse) {
            if (Array.isArray(curriculumResponse[key])) {
              syllabusData = curriculumResponse[key];
              break;
            }
          }
        }

        if (syllabusData.length > 0) {
          const isNewStructure = syllabusData.some(item => item.videos && Array.isArray(item.videos));
          
          let parsedTopics = [];
          if (isNewStructure) {
            parsedTopics = syllabusData.map((syllabusItem, idx) => {
               const videos = syllabusItem.videos || [];
               const mappedVideos = videos.map((video, vIdx) => {
                  const vidId = video.videoid || `${syllabusItem.id || idx}-${vIdx}`;
                  const pData = progressMap[`${courseId}_${vidId}`] || progressMap[vidId] || {};
                  return {
                    id: vidId,
                    title: video.videotitle || video.title || `Video ${vIdx + 1}`,
                    duration: video.duration || '0:00',
                    videoUrl: getFullAssetUrl(video.videourl || video.url || video.video_url),
                    description: video.videodescription || video.description || '',
                    isCompleted: pData.iscompleted === true || pData.iscompleted === "true" || Math.round(pData.progress_percent || 0) >= 100,
                    progressPercent: pData.progress_percent || 0,
                    lastPosition: pData.last_position_seconds || pData.watched_seconds || pData.watchedTime || 0,
                    variants: video.variants || video.attachments || []
                  };
               });
               
               return {
                  id: syllabusItem.id || `syllabus-${idx}`,
                  title: syllabusItem.title || syllabusItem.module_title || `Module ${idx + 1}`,
                  items: mappedVideos,
                  variants: syllabusItem.variants || syllabusItem.attachments || [],
                  duration: syllabusItem.duration || '',
                  description: syllabusItem.description || ''
               };
            });
          } else {
            const mappedItems = syllabusData.map((item, index) => {
              const pData = progressMap[`${courseId}_${item.curriculamid || item.id}`] || progressMap[item.curriculamid || item.id] || {};
              return {
                id: item.curriculamid || item.id || `item-${index}`,
                title: item.title || item.module_title || `Module ${index + 1}`,
                duration: item.duration || '0:00',
                videoUrl: getFullAssetUrl(item.url || item.video_url || item.videoUrl),
                isCompleted: pData.iscompleted === true || pData.iscompleted === "true" || Math.round(pData.progress_percent || 0) >= 100,
                progressPercent: pData.progress_percent || 0,
                lastPosition: pData.last_position_seconds || pData.watched_seconds || pData.watchedTime || 0,
                description: item.description || '',
                variants: item.variants || item.attachments || []
              };
            });

            parsedTopics = [{
              id: 'main-section',
              title: 'Course Content',
              isExpanded: true,
              items: mappedItems,
              variants: []
            }];
          }

          setTopics(parsedTopics);
          if (parsedTopics.length > 0) {
            setExpandedSyllabus(parsedTopics[0].id);
            if (parsedTopics[0].items && parsedTopics[0].items.length > 0) {
              setSelectedVideo(parsedTopics[0].items[0]);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching course detail:", error);
        toast.error("Failed to load course content");
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      fetchData();
    }
  }, [courseId]);

  // Load saved progress when a video is selected (initial load or change)
  useEffect(() => {
    if (!selectedVideo?.videoUrl) return;
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const watchedTime = Number(selectedVideo.lastPosition) || 0;
    if (watchedTime > 0) {
      const seekToSaved = () => {
        videoEl.currentTime = watchedTime;
        setProgress(watchedTime);
        setCurrentTimeStr(formatTime(watchedTime));
      };
      if (videoEl.readyState >= 1) {
        seekToSaved();
      } else {
        videoEl.addEventListener('loadedmetadata', seekToSaved, { once: true });
      }
    } else {
      setProgress(0);
      setCurrentTimeStr('0:00');
    }
  }, [selectedVideo]);

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return '0:00';
    const m = Math.floor(timeInSeconds / 60);
    const s = Math.floor(timeInSeconds % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setIsMuted(val === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
      if (newMuted) setVolume(0);
      else {
        videoRef.current.volume = 1;
        setVolume(1);
      }
    }
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setProgress(time);
      setCurrentTimeStr(formatTime(time));
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      playerContainerRef.current?.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const changePlaybackRate = (rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) videoRef.current.playbackRate = rate;
    setShowSpeedMenu(false);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  const handleMouseLeave = () => {
    if (isPlaying) setShowControls(false);
  };

  const handleVideoSelect = (item) => {
    setSelectedVideo(item);
    setIsPlaying(false);
    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const toggleSyllabusExpand = (itemId) => {
    setExpandedSyllabus(prev => prev === itemId ? null : itemId);
  };

  const handleBackToCourse = () => {
    navigate('/mycourse');
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
          <p className="text-slate-500 font-bold animate-pulse">Loading course content...</p>
        </div>
      </div>
    );
  }

  const currentSyllabus = topics.find(t => t.id === expandedSyllabus) || topics[0];
  const displayTitle = selectedVideo?.title || currentSyllabus?.title || courseInfo?.coursename;
  const displayDuration = selectedVideo?.duration || currentSyllabus?.duration || "0:00";
  const displayDescription = selectedVideo?.description || currentSyllabus?.description || "In this module, we dive deep into the fundamental concepts of the course. Stay tuned for expert insights and practical demonstrations.";
  const displayVariants = (selectedVideo?.variants && selectedVideo.variants.length > 0) ? selectedVideo.variants : (currentSyllabus?.variants || []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      <CourseHeader />

      <div className="pt-24 pb-12">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button 
                onClick={handleBackToCourse}
                className="p-2.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:text-orange-600 hover:border-orange-200 transition-all active:scale-95"
              >
                <ArrowLeft size={18} />
              </button>
              <nav className="flex items-center gap-2 text-sm font-medium">
                <span className="text-slate-400 hover:text-slate-600 cursor-pointer" onClick={() => navigate('/mycourse')}>My Courses</span>
                <ChevronRight size={14} className="text-slate-300" />
                <span className="text-slate-800 line-clamp-1 max-w-[200px] sm:max-w-none">
                  {courseInfo?.coursename || "Course Details"}
                </span>
              </nav>
            </div>
          </div>

          <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 lg:items-stretch mb-10">
            
            {/* Main Content: Player Area (8 columns) */}
            <div className="lg:col-span-8">
              {/* Video Player Container */}
              <div 
                ref={playerContainerRef}
                className="relative group rounded-lg overflow-hidden bg-slate-900 h-full flex flex-col items-center justify-center"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                <div className="aspect-video relative h-full w-full flex items-center justify-center">
                  {selectedVideo?.videoUrl ? (
                    <>
                      <video
                        ref={videoRef}
                        id="course-video"
                        key={selectedVideo.id}
                        disablePictureInPicture
                        playsInline
                        preload="auto"
                        onPlay={() => setIsPlaying(true)}
                        onTimeUpdate={(e) => {
                          const current = e.target.currentTime;
                          const duration = e.target.duration;
                          setProgress(current);
                          setCurrentTimeStr(formatTime(current));
                          sendProgress(current, duration);
                        }}
                        onLoadedMetadata={(e) => {
                          setDurationStr(formatTime(e.target.duration));
                        }}
                        onPause={(e) => {
                          setIsPlaying(false);
                          const current = e.target.currentTime;
                          const duration = e.target.duration;
                          lastSaveTimeRef.current = 0;
                          sendProgress(current, duration);
                        }}
                        onClick={togglePlay}
                        className="w-full h-full object-contain cursor-pointer"
                        src={selectedVideo.videoUrl}
                      >
                        Your browser does not support the video tag.
                      </video>
                      
                      {/* Controls Overlay */}
                      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 transition-opacity duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}>
                        {/* Progress Bar */}
                        <div className="flex items-center gap-2 mb-2">
                          <input 
                            type="range" 
                            min="0" 
                            max={videoRef.current?.duration || 0} 
                            value={progress || 0} 
                            onChange={handleSeek}
                            className="w-full h-1.5 bg-slate-600 rounded-lg cursor-pointer accent-red-500 hover:h-2 transition-all"
                          />
                        </div>
                        
                        <div className="flex items-center justify-between text-white mt-2">
                          <div className="flex items-center gap-4">
                            <button onClick={togglePlay} className="hover:text-red-500 transition cursor-pointer">
                              {isPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" />}
                            </button>
                            
                            <span className="text-sm font-medium">
                              {currentTimeStr} / {durationStr}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <button onClick={toggleMute} className="hover: transition cursor-pointer">
                                {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                              </button>
                                          {/* Volume slider hidden per request */}
                            </div>

                            <div className="relative">
                              <button 
                                onClick={() => setShowSpeedMenu(!showSpeedMenu)} 
                                className={` transition flex items-center gap-1 cursor-pointer`}
                                title="Playback Speed"
                              >
                                <PlaybackSpeedIcon size={20} />
                                {playbackRate !== 1 && <span className="text-xs font-bold">{playbackRate}x</span>}
                              </button>
                              
                              {showSpeedMenu && (
                                <div className="absolute bottom-full right-0 mb-4 bg-slate-900/95 backdrop-blur border border-slate-700 rounded-lg py-2 flex flex-col shadow-xl min-w-[100px] z-50">
                                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                                    <button 
                                      key={rate} 
                                      onClick={() => changePlaybackRate(rate)}
                                      className={`px-4 py-2 text-sm text-left hover:bg-slate-800 transition cursor-pointer ${playbackRate === rate ? 'text-red-500 font-bold' : 'text-white'}`}
                                    >
                                      {rate === 1 ? 'Normal' : `${rate}x`}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            <button onClick={toggleFullscreen} className="hover:text-red-500 transition cursor-pointer">
                              <Maximize size={20} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8 text-center bg-slate-900">
                      <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                        <Play size={40}  />
                      </div>
                      <h3 className="text-xl font-bold mb-2">
                        {selectedVideo ? "No Video Available" : "Select a Lesson"}
                      </h3>
                      <p className="text-slate-400 text-sm max-w-xs">
                        {selectedVideo ? "This module doesn't have a video associated with it." : "Pick a video from the syllabus on the right to start your learning journey."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col h-full min-h-[450px]">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                   <h3 className="text-md font-black text-slate-800 flex items-center gap-2">
                      <BookOpen size={20} className="text-orange-500" />
                      Syllabus
                   </h3>
                </div>
                
                <div className="flex-1 overflow-y-auto no-scrollbar">
                  <div className="divide-y divide-slate-100">
                    {topics.map(topic => (
                      <div key={topic.id} className="flex flex-col border-t border-slate-50">
                        {/* Syllabus Header */}
                        <div
                          className={`group flex items-center gap-4 p-4 hover:bg-slate-50 transition-all cursor-pointer ${expandedSyllabus === topic.id ? 'bg-slate-50' : ''}`}
                          onClick={() => toggleSyllabusExpand(topic.id)}
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${expandedSyllabus === topic.id ? 'bg-orange-100 text-orange-500' : 'bg-slate-100 text-slate-500'}`}>
                            <BookOpen size={16} />
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col">
                            <p className={`text-sm font-bold truncate ${expandedSyllabus === topic.id ? 'text-orange-600' : 'text-slate-700'}`}>
                              {topic.title}
                            </p>
                            {topic.duration && (
                              <div className="flex items-center gap-3 mt-1 text-[10px] font-medium text-slate-400">
                                <span className="flex items-center gap-1"><Clock size={10} /> {topic.duration}</span>
                              </div>
                            )}
                          </div>
                          {expandedSyllabus === topic.id ? (
                            <ChevronDown size={14} className="text-orange-500 transition-transform" />
                          ) : (
                            <ChevronRight size={14} className="text-slate-300 group-hover:translate-x-1 transition-transform" />
                          )}
                        </div>

                        {/* Video Items (Expandable Content) */}
                        {expandedSyllabus === topic.id && (
                          topic.items && topic.items.length > 0 ? (
                            topic.items.map(video => (
                              <div 
                                key={video.id}
                                className={`pl-16 pr-4 py-3 cursor-pointer transition-all ${selectedVideo?.id === video.id ? 'bg-orange-50/50 border-l-2 border-orange-500' : 'hover:bg-slate-50 border-l-2 border-transparent'}`}
                                onClick={() => handleVideoSelect(video)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${selectedVideo?.id === video.id ? 'bg-orange-500 text-white shadow-sm' : 'bg-slate-200 text-slate-500'}`}>
                                    <Play size={12} className="ml-0.5" fill={selectedVideo?.id === video.id ? "currentColor" : "none"} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-bold truncate ${selectedVideo?.id === video.id ? 'text-orange-600' : 'text-slate-600'}`}>
                                      {video.title}
                                    </p>
                                    <div className="flex items-center gap-3 mt-0.5 text-[9px] font-medium text-slate-400">
                                      {video.duration && (
                                        <span className="flex items-center gap-1"><Clock size={10} /> {video.duration}</span>
                                      )}
                                      {video.progressPercent > 0 && !video.isCompleted && (
                                        <span className="text-orange-500 font-bold">{Math.round(video.progressPercent)}%</span>
                                      )}
                                      {video.isCompleted && (
                                        <span className="flex items-center gap-1 text-green-500 font-bold">
                                          <CheckCircle size={10} className="text-green-500" /> Completed
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="pl-16 pr-4 py-4 text-sm text-slate-500 italic flex items-center gap-2">
                              <Info size={16} className="text-slate-400" />
                              No video available
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Full Width Overview Box */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="border-b border-slate-100 px-8 py-2">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 ">
                <h1 className="text-md font-black text-slate-800 leading-tight">
                  {displayTitle}
                </h1>
                <div className="flex items-center gap-4 text-sm font-bold text-slate-500">
                  <div className="flex items-center text-sm gap-1.5 px-2 py-1 rounded-lg bg-slate-50 border border-slate-100">
                    <Clock size={15} className="text-orange-500" />
                    {displayDuration}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-8 py-3">
              <div className="prose prose-slate max-w-none text-sm sm:text-base">
                <p className="text-slate-600 leading-relaxed text-sm mb-2">
                  {displayDescription}
                </p>
              </div>

              {displayVariants && displayVariants.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-gray-200">
                  <h3 className="text-sm font-bold text-slate-800">Attachments</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {displayVariants.map((v, idx) => (
                      <a 
                        key={idx} 
                        href={getAttachmentUrl(v) || '#'}
                        download={v.name || v.fileurl?.split('/').pop() || 'Attachment'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (!getAttachmentUrl(v)) {
                            e.preventDefault();
                            toast.error("Download link not available");
                          }
                        }}
                        className="flex items-center gap-2.5 p-2 bg-slate-50 border border-slate-100 rounded-xl hover:border-orange-200 hover:bg-orange-50/20 transition-all cursor-pointer group max-w-[220px]"
                        title={v.name || v.fileurl?.split('/').pop() || 'Download Resource'}
                      >
                        <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600 shrink-0 group-hover:scale-105 transition-transform">
                          <Download size={18} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-800 truncate">
                            {(v.name || v.fileurl?.split('/').pop() || 'Resource File').length > 18 
                              ? (v.name || v.fileurl?.split('/').pop() || 'Resource File').substring(0, 18) + '...' 
                              : (v.name || v.fileurl?.split('/').pop() || 'Resource File')}
                          </p>
                          <p className="text-[10px] text-blue-400/80 font-medium">Attachment</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;